import React from 'react';
import AgencyNavbar from '../components/layout/AgencyNavbar';
import AgencyFooter from '../components/layout/AgencyFooter';
import { CheckCircle2, Target, TrendingUp, Users } from 'lucide-react';
import { useSiteContent } from '../contexts/SiteContentContext';

const SolucionesPage = () => {
  const { getPageContent } = useSiteContent();
  const content = getPageContent('soluciones') || {};

  const hero = content.hero || {};
  const valueProps = content.value_props || [];
  const strategy = content.strategy || {};
  const cta = content.cta || {};

  const getIcon = (iconType, index) => {
    if (iconType === '0%') return <span className="text-4xl font-black text-black">0%</span>;
    if (iconType === 'target') return <Target className="w-8 h-8 text-white" />;
    if (iconType === 'trending') return <TrendingUp className="w-8 h-8 text-black" />;
    return <span className="text-4xl font-black text-black">{index + 1}</span>;
  };

  const getIconBg = (index) => {
    const colors = ['#E9FF60', '#FF3131', '#E9FF60'];
    return colors[index % colors.length];
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <AgencyNavbar />
      
      <div className="pt-24 pb-16 px-4">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h1 
            className="text-5xl md:text-7xl font-black mb-8 tracking-tighter"
            style={{ fontFamily: 'Unbounded, Montserrat, sans-serif' }}
            data-testid="soluciones-title"
          >
            <span className="text-[#E9FF60]">{hero.title_1 || 'HACKEAMOS'}</span>
            <br />
            <span className="text-white">{hero.title_2 || 'EL SISTEMA PARA QUE VOS'}</span>
            <br />
            <span className="text-[#FF3131]">{hero.title_3 || 'RECUPERES EL CONTROL'}</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-6" data-testid="soluciones-subtitle">
            {hero.subtitle || 'No somos un marketplace. Somos una '}
            <span className="text-[#E9FF60] font-bold">{hero.subtitle_accent || 'fábrica de soberanía digital'}</span>.
          </p>
          
          <p className="text-lg text-gray-400" data-testid="soluciones-description">
            {hero.description || 'Te ayudamos a migrar tus clientes desde las apps-delivery con comisiones abusivas a tu propio sistema, donde vos sos el dueño.'}
          </p>
        </div>

        {/* Value Propositions */}
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {(valueProps.length > 0 ? valueProps : [
              {icon: '0%', title: '0% Comisiones', description: 'El 100% de la venta es para vos. No más perder hasta un 30% de tus ingresos en comisiones de intermediarios.'},
              {icon: 'target', title: 'Canal Directo', description: 'Pedidos automáticos por WhatsApp sin intermediarios. Contacto directo con tu cliente, sin pérdida de información.'},
              {icon: 'trending', title: 'Tu Marca, Tu App', description: 'No sos un logo más en una lista, sos el protagonista. Tu propia URL, tus colores, tu identidad.'}
            ]).map((prop, i) => (
              <div 
                key={i}
                className="bg-[#1C1C1C] border border-[#333333] rounded-lg p-8 hover:border-[#E9FF60] transition-all" 
                data-testid={`value-prop-${i+1}`}
              >
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                  style={{ backgroundColor: getIconBg(i) }}
                >
                  {getIcon(prop.icon, i)}
                </div>
                <h3 className="text-2xl font-bold mb-4 text-[#E9FF60]">{prop.title}</h3>
                <p className="text-gray-300">{prop.description}</p>
              </div>
            ))}
          </div>

          {/* Strategy Section */}
          <div className="bg-[#1C1C1C] border border-[#E9FF60] rounded-lg p-10 mb-16">
            <div className="flex items-start gap-6">
              <div className="bg-[#E9FF60] w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-10 h-10 text-black" />
              </div>
              <div>
                <h2 className="text-3xl font-black mb-4 text-[#E9FF60]" data-testid="strategy-title">
                  {strategy.title || 'ESTRATEGIA DE MIGRACIÓN INCLUIDA'}
                </h2>
                <p className="text-lg text-gray-300 mb-4">
                  {strategy.description || 'No te dejamos solo. Te acompañamos en el proceso de migrar tus clientes desde las apps-delivery con comisiones a tu propio sistema.'}
                </p>
                <ul className="space-y-3">
                  {(strategy.items || [
                    'Campañas de comunicación para anunciar tu nueva plataforma',
                    'Incentivos y promociones para tus clientes habituales',
                    'Capacitación en uso de la plataforma y gestión de pedidos',
                    'Seguimiento y optimización durante la transición'
                  ]).map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#E9FF60] mt-1 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <h2 className="text-4xl font-black mb-6 text-white" data-testid="cta-title">
              {cta.title || '¿LISTO PARA '}<span className="text-[#E9FF60]">RECUPERAR</span> TU INDEPENDENCIA?
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/demo"
                className="inline-block bg-[#FF3131] hover:bg-[#ff4545] text-white px-10 py-4 rounded-full text-lg font-bold"
                data-testid="cta-demo-button"
              >
                {cta.button_demo || 'Solicitá una Demo'}
              </a>
              <a
                href="/planes"
                className="inline-block bg-[#E9FF60] hover:bg-[#f0ff80] text-black px-10 py-4 rounded-full text-lg font-bold"
                data-testid="cta-planes-button"
              >
                {cta.button_planes || 'Ver Planes y Precios'}
              </a>
            </div>
          </div>
        </div>
      </div>

      <AgencyFooter />
    </div>
  );
};

export default SolucionesPage;
